// The cuadre process view's pure half — `getCierreClosingDetail`'s response shaped for
// `ClosingProcessPanel`. Same convention as `cierreCorrections.js`: what breaks silently here is
// worth testing without a DOM or a backend.

// `closings.state` (8 values, `mantis/src/services/cierres/schedule/state.js`) -> a Spanish
// label and a tone for the banner. Not the panel's own binary "Good to Go!" / "Pendiente
// revisión" vocabulary (`panel/clients/davivienda.yaml`, per client) — this is the more granular
// process-view state, read off the closing row itself rather than a per-client label.
export const STATE_LABELS = {
  SCHEDULED: { label: "Programado", tone: "pending" },
  RUNNING: { label: "En curso", tone: "pending" },
  BALANCED: { label: "Cuadrado", tone: "ok" },
  NEEDS_REVIEW: { label: "Necesita revisión", tone: "warn" },
  PARTIAL: { label: "Parcial", tone: "warn" },
  RESOLVED: { label: "Resuelto", tone: "ok" },
  FAILED: { label: "Falló", tone: "warn" },
  CANCELLED: { label: "Cancelado", tone: "pending" },
};

// `closing_alerts.severity` -> the three buckets the alerts pane already renders
// (`cierreCorrections.js`'s `SEVERITY_TYPES`). Reused here rather than re-declared so the two
// views never disagree about which severities block.
export const SEVERITY_TYPES = {
  alert: "ALERT",
  blocked: "ALERT",
  subalert: "SUBALERT",
  info: "INFO",
  // A difference this closing inherited from an earlier day: shown here, attended on that day.
  carried: "CARRIED",
};

const TERMINAL_OK_STATES = ["BALANCED", "RESOLVED"];

// A closing is CUADRE EXITOSO when it reached a terminal "ok" state AND nothing blocking is
// still open. Brinks' own note (quoted in the panel): "las alertas de documentos faltantes o
// fechas incorrectas deben bloquear el cierre/reporte final; las diferencias de conciliación
// deben ser informativas y visibles" — an open `info`/`subalert` (a conciliation difference)
// does not stop this from being true; an open `alert`/`blocked` does.
export const isCuadreExitoso = (detail) => {
  const state = detail?.closing?.state;
  if (!TERMINAL_OK_STATES.includes(state)) return false;

  const alerts = detail?.alerts || [];
  return !alerts.some(
    (alert) => alert?.status === "OPEN" && SEVERITY_TYPES[alert?.severity] === "ALERT"
  );
};

// The state banner's label + tone, "CUADRE EXITOSO" taking priority over the raw state label —
// a BALANCED closing with an open blocking alert is not what an operator would call cuadrado,
// even though the row's own state column still says so until someone reopens it.
export const stateBanner = (detail) => {
  if (isCuadreExitoso(detail)) return { label: "CUADRE EXITOSO", tone: "ok" };
  const state = detail?.closing?.state;
  return STATE_LABELS[state] || { label: state || "-", tone: "pending" };
};

// One `sources[]` entry -> the step row the panel draws. A source with no files and no live
// datapoints is the visible sign of a fetch/extraction that never produced anything, without
// having to read `closing_events` to notice.
export const toStepView = (source) => ({
  key: source?.id,
  label: source?.sourceKey || source?.id,
  status: source?.status || null,
  error: source?.error || null,
  fileCount: (source?.files || []).length,
  datapointCount: source?.datapointCount || 0,
  ok: Boolean((source?.files || []).length || source?.datapointCount),
});

// One `calculations[]` entry -> the result row the panel draws. `current` is always present
// (`foldCalculations` on the backend guarantees it); a calculation with no resolved value yet is
// shown as such rather than folded into a "0" that would misread as a real comparison.
export const toResultView = (calculation) => ({
  key: calculation?.key,
  value: calculation?.current?.valueNumeric ?? null,
  resolved: Boolean(calculation?.current?.resolved),
  changed: Boolean(calculation?.changed),
  missingInputs: calculation?.current?.missingInputs || [],
});
